import {Injectable} from '@angular/core';
import {IccDrawMathService, IccDrawSyncService, IccDrawDetails} from '@icc/draw';

export class DrawData {
    innerEdge: any;
    outerEdge: any;

    direction: string;
    length: number;

    poly: any[];
    polyArea: number;
    polyEdge: number;

    rect: any;
    rectArea: number;
    rectEdge: number;

    constructor(poly: any[], params: any = {}, getInfo?: boolean) {
        (<any>Object).assign(this, params);

        this.poly = poly;
        this.polyArea = IccDrawMathService.getAreaFromPoly(this.poly) / 1e6;
        this.polyEdge = IccDrawMathService.getPerimeterFromPoly(this.poly) / 1e3;

        this.rect = IccDrawMathService.getRectFromPoly(this.poly);
        this.rectArea = IccDrawMathService.getAreaFromRect(this.rect) / 1e6;
        this.rectEdge = IccDrawMathService.getPerimeterFromRect(this.rect) / 1e3;

        if (getInfo) {
            this.innerEdge = IccDrawMathService.getLineInfo([poly[2], poly[3]]);
            this.outerEdge = IccDrawMathService.getLineInfo([poly[0], poly[1]]);
        }

        if (params.direction) {
            this.length = params.length;
        }
    }
}

@Injectable()
export class DrawService {
    details: any = {};
    options: any = {
        base: {
            indexes: false,
            side: 'inner',
        },
        dimensions: {
            multiplier: 1.5,
            ratio: 1,
        },
        indexes: false,
        onInterfaceClick: () => {},
    };

    data: any;
    node: any;

    get(type: string, params?: any): any {
        if (type && this.data && this.data[type] && this.data[type].length) {
            return this.data[type].find(el => params ? Object.keys(params).reduce((prev, curr) => prev && el[curr] === params[curr], true) : true) || null;
        } else {
            return null;
        }
    }

    getData(details: any): any[] {
        try {
            this.details = new IccDrawDetails(details);

            this.resetNodeData();
            this.parseNodeData();

            return (<any>Object).assign({}, this.data);
        } catch (err) {
            console.error(err);
        }

        return null;
    }

    resetNodeData() {
        const options = {
            base: (<any>Object).assign({}, this.options.base, {
                side: null,
            }),
            dimensions: false,
        };

        this.node = IccDrawSyncService.syncDetails(this.details, options);

        this.data = {
            alignment: [],
            extension: [],
            filling: [],
            frame: [],
            glazingBead: [],
            mullion: [],
            muntin: [],
            reno: [],
            renson: [],
            sash: [],
            sashFrame: [],
            shape: [],
        };
    }

    parseNodeData(node: any = this.node) {
        if (node) {
            const functionName = node.constructor.name ? `parse${node.constructor.name}Data` : null;

            if (functionName && typeof this[functionName] === 'function') {
                this[functionName](node);
            }

            if (node.children && node.children.length > 0) {
                node.children.map(child => this.parseNodeData(child));
            }
        }
    }

    parseAlignmentNodeData(node: any) {
        this.data.alignment.push(new DrawData(node.poly, {
            alignmentId: node.alignment ? node.alignment.id : null,
            direction: node.alignment ? node.alignment.direction : null,
            sashId: node.sash ? node.sash.id : null,
        }));
    }

    parseExtensionsNodeData(node: any) {
        node.extensions.map(extension => {
            this.data.extension.push(new DrawData(extension.poly, {
                direction: ['top', 'bottom'].indexOf(extension.side) !== -1 ? 'horizontal' : 'vertical',
                extensionId: extension.extension ? extension.extension.id : null,
            }));
        });
    }

    parseFrameNodeData(node: any) {
        this.data.frame.push({
            inner: new DrawData(node.poly),
            outer: new DrawData(node.parent.poly),
            sides: node.polygons.map(poly => new DrawData(poly, {}, true)),
        });
    }

    parseGlazingNodeData(node: any) {
        this.data.filling.push(new DrawData(node.poly, {
            sashId: node.sash ? node.sash.id : null,
        }));
    }

    parseGlazingBeadNodeData(node: any) {
        this.data.glazingBead.push({
            inner: new DrawData(node.poly),
            outer: new DrawData(node.parent.poly),
            sides: node.polygons.map(poly => new DrawData(poly, {}, true)),
            sashId: node.sash ? node.sash.id : null,
        });
    }

    parseMullionNodeData(node: any) {
        this.data.mullion.push(new DrawData(node.poly, {
            direction: node.mullion ? node.mullion.direction : null,
            mullionId: node.mullion ? node.mullion.id : null,
            mullionLength: node.mullion ? node.mullion.length : null,
            sashId: node.sash ? node.sash.id : null,
        }));
    }

    parseMuntinNodeData(node: any) {
        this.data.muntin.push(new DrawData(node.poly, {
            muntinId: node.muntin ? node.muntin.id : null,
            sashId: node.sash ? node.sash.id : null,
        }));
    }

    parseRenoNodeData(node: any) {
        this.data.reno.push(new DrawData(node.poly));
    }

    parseRensonNodeData(node: any) {
        this.data.renson.push(new DrawData(node.poly, {
            rensonId: node.renson ? node.renson.id : null,
            sashId: node.sash ? node.sash.id : null,
        }));
    }

    parseSashFrameNodeData(node: any) {
        this.data.sashFrame.push({
            inner: new DrawData(node.poly),
            outer: new DrawData(node.parentPoly),
            sides: node.polygons.map(poly => new DrawData(poly, {}, true)),
            sashId: node.sash ? node.sash.id : null,
        });
    }

    parseSashNodeData(node: any) {
        const poly = IccDrawMathService.intersectPolyWithRect(this.data.shape[0].poly, node.rect);
        const parallelPoly = IccDrawMathService.getParallelPoly(node.poly, node.parent.poly);
        const parallelProfiles = parallelPoly.map(i => (<any>Number).isInteger(i) ? (node.parent.profiles[i] || node.parent.profiles[0]).profileId : null);

        this.data.sash.push({
            inner: new DrawData(node.poly),
            outer: new DrawData(poly),
            sashId: node.sash ? node.sash.id : null,
            parallel: {
                poly: parallelPoly || [],
                profiles: parallelProfiles || [],
            },
        });
    }

    parseShapeNodeData(node: any) {
        this.data.shape.push(new DrawData(node.poly));
    }
}
